import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { authorityApi, SCOPE_LABELS, Row } from '../../../api/authority';
import { AuthorityNav, errMsg } from './authorityUi';

/** Friendly capability bundles ("Finance - Officer"). Technical permission keys stay authoritative underneath. */
export function AccessProfilesPage() {
  const canManage = usePermission('admin.authority.manage');
  const advanced = usePermission('admin.security.advanced');
  const qc = useQueryClient(); const { showSuccess, showError } = useToast();
  const list = useQuery({ queryKey: ['authority-profiles'], queryFn: () => authorityApi.accessProfiles() });
  const [editing, setEditing] = useState<Row | null>(null);
  const grouped = useMemo(() => (list.data?.data ?? []).reduce<Record<string, Row[]>>((a, r) => { (a[r.profile_module ?? 'other'] ||= []).push(r); return a; }, {}), [list.data]);
  const save = useMutation({ mutationFn: (b: Row) => authorityApi.saveAccessProfile(editing?.id ?? null, b), onSuccess: () => { showSuccess('Access Profile saved.'); setEditing(null); qc.invalidateQueries({ queryKey: ['authority-profiles'] }); }, onError: (e) => showError(errMsg(e, 'Failed to save.')) });
  return (
    <div className="page">
      <PageHeader title="Access Profiles" description="What a position can do in each module, in plain language. Give profiles to positions - never to individual people." actions={canManage ? <button onClick={() => setEditing({})}>+ New Access Profile</button> : undefined} />
      <AuthorityNav current="/admin/authority/access-profiles" />
      {Object.entries(grouped).map(([mod, profiles]) => (
        <div className="card" key={mod}>
          <h2 style={{ textTransform: 'capitalize' }}>{mod.replace(/_/g, ' ')}</h2>
          <table className="data-table"><thead><tr><th>Access Level</th><th>What it allows</th><th>Positions</th>{canManage && <th />}</tr></thead><tbody>
            {profiles.map((p) => (
              <tr key={p.id}>
                <td><strong>{p.profile_level_label ?? p.label}</strong><div className="muted" style={{ fontSize: 12 }}>{p.label}</div></td>
                <td style={{ fontSize: 12.5 }}>{(p.permissions as Row[]).map((x) => `${x.label ?? x.permission_key} - ${SCOPE_LABELS[x.scope] ?? x.scope}`).join('; ')}
                  {advanced && <div className="muted" style={{ fontSize: 11 }}>{(p.permissions as Row[]).map((x) => x.permission_key).join(', ')}</div>}</td>
                <td>{p.position_count}</td>
                {canManage && <td><button className="link-button" onClick={() => setEditing(p)}>Edit</button></td>}
              </tr>
            ))}
          </tbody></table>
        </div>
      ))}
      {!list.isLoading && !list.data?.data.length && <div className="card"><p className="muted">No Access Profiles yet. Reconciliation can show which existing access patterns are shared by whole positions.</p></div>}
      {editing && <Modal title={editing.id ? `Edit ${editing.label}` : 'New Access Profile'} onClose={() => setEditing(null)} wide><ProfileBuilder initial={editing} onSave={(b) => save.mutate(b)} saving={save.isPending} /></Modal>}
    </div>
  );
}

function ProfileBuilder({ initial, onSave, saving }: { initial: Row; onSave: (b: Row) => void; saving: boolean }) {
  const cat = useQuery({ queryKey: ['authority-catalogue'], queryFn: () => authorityApi.catalogue() });
  const [module, setModule] = useState<string>(initial.profile_module ?? '');
  const [level, setLevel] = useState<string>(initial.profile_level_label ?? '');
  const [label, setLabel] = useState<string>(initial.label ?? '');
  const [sel, setSel] = useState<Record<string, string>>(Object.fromEntries(((initial.permissions as Row[]) ?? []).map((p) => [p.permission_key, p.scope])));
  const [filter, setFilter] = useState('');
  const byModule = (cat.data?.data ?? []).filter((c) => !c.code.startsWith('admin.security') && (!filter || `${c.label} ${c.module} ${c.code}`.toLowerCase().includes(filter.toLowerCase())))
    .reduce<Record<string, Row[]>>((a, c) => { (a[c.module] ||= []).push(c); return a; }, {});
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ label: label || `${module} - ${level}`, module, levelLabel: level, permissions: Object.entries(sel).map(([key, scope]) => ({ key, scope })) }); }}>
      <div className="form-row">
        <label>Module<input required value={module} onChange={(e) => setModule(e.target.value)} placeholder="e.g. procurement" /></label>
        <label>Access Level<input required value={level} onChange={(e) => setLevel(e.target.value)} placeholder="e.g. Officer, Manager, Review, View" /></label>
        <label>Display Name<input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Procurement - Officer" /></label>
      </div>
      <input placeholder="Filter capabilities…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ maxWidth: 260, margin: '6px 0' }} />
      <div className="profile-builder">
        {Object.entries(byModule).map(([m, caps]) => (
          <fieldset key={m}><legend>{m}</legend>
            {caps.map((c) => (
              <div key={c.code} className="profile-cap">
                <label className="discussion-checkbox"><input type="checkbox" checked={c.code in sel} onChange={(e) => { const n = { ...sel }; if (e.target.checked) n[c.code] = 'all'; else delete n[c.code]; setSel(n); }} /> {c.label}</label>
                {c.code in sel && <select value={sel[c.code]} onChange={(e) => setSel({ ...sel, [c.code]: e.target.value })}>{Object.entries(SCOPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>}
              </div>
            ))}
          </fieldset>
        ))}
      </div>
      <p className="muted" style={{ fontSize: 12 }}>{Object.keys(sel).length} capabilities selected. Changes apply to every holder of every position using this profile.</p>
      <div className="form-actions"><button type="submit" disabled={saving || !Object.keys(sel).length}>{saving ? 'Saving…' : 'Save Access Profile'}</button></div>
    </form>
  );
}
